import { useState } from 'react'
import { X, Receipt, Loader2, User } from 'lucide-react'
import { useHeldReceipts } from '@/api/receipts'
import { useUIStore } from '@/stores/uiStore'
import { useRecallReceipt } from '@/hooks'
import { formatPrice } from '@/lib/utils'

export function HeldReceiptsModal() {
  const { closeModal, showToast } = useUIStore()
  const { data: heldReceipts = [], isLoading, isFetching } = useHeldReceipts()
  const { recall } = useRecallReceipt()
  const [recallingId, setRecallingId] = useState<number | null>(null)

  const showLoader = isLoading || isFetching

  const handleRecall = async (receiptId: number) => {
    if (recallingId) return
    setRecallingId(receiptId)

    try {
      await recall(receiptId)
      closeModal()
      showToast({ type: 'success', title: 'Receipt Recalled', message: 'Held receipt has been resumed.' })
    } catch {
      setRecallingId(null)
      showToast({ type: 'error', title: 'Recall Failed', message: 'Could not recall the receipt. Please try again.' })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Held Receipts</h2>
          <button
            onClick={closeModal}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {showLoader && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="text-green-500 animate-spin" />
            </div>
          )}

          {!showLoader && heldReceipts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Receipt size={32} className="mb-2" />
              <p className="font-medium">No held receipts</p>
            </div>
          )}

          {!showLoader && heldReceipts.length > 0 && (
            <div className="space-y-2">
              {heldReceipts.map((receipt) => {
                const isRecalling = recallingId === receipt.id

                const ts = new Date(receipt.timestamp)
                const datePart = ts
                  .toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  .replace(',', '')
                const timePart = ts
                  .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
                  .toLowerCase()
                  .replace(' ', '')

                return (
                  <button
                    key={receipt.id}
                    onClick={() => handleRecall(receipt.id)}
                    disabled={!!recallingId}
                    className="w-full p-4 bg-gray-50 hover:bg-green-50 hover:ring-2 hover:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed rounded-xl flex items-center gap-4 transition-all text-left cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {isRecalling
                        ? <Loader2 size={20} className="text-blue-600 animate-spin" />
                        : <Receipt size={20} className="text-blue-600" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">#{receipt.number ?? receipt.id}</span>
                        {receipt.customer && (
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <User size={12} />
                            {receipt.customer.display_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {datePart} @ {timePart}
                      </div>
                    </div>
                    <span className="text-green-600 font-semibold flex-shrink-0">
                      ${formatPrice(Number(receipt.totals?.total ?? 0))}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
